<template>
  <Modal :title="`Sales Play: ${data.sales_play_name || 'Status Change'}`" :max-width="560" @close="onClose">
    <div class="psc-body">
      <div class="psc-fields">
        <div v-if="data.kind" class="psc-row"><span class="psc-label">Action</span> {{ data.kind }}</div>
        <div v-if="triggeredBy || runTypeLabel || data.actor_user_name" class="psc-row">
          <span class="psc-label">By</span>
          <span v-if="data.actor_user_name" class="psc-type-badge psc-type--user">User</span>
          <span v-else-if="runTypeLabel" class="psc-type-badge" :class="`psc-type--${data.agent_run_type}`">{{ runTypeLabel }}</span>
          <span v-if="triggeredBy"> {{ triggeredBy }}</span>
        </div>
        <div v-if="timestamp" class="psc-row"><span class="psc-label">On</span> {{ timestamp }}</div>
      </div>

      <div v-if="data.reason || data.detail" class="psc-detail">
        <div v-if="data.actor_user_id" class="psc-manual-tag">manual</div>
        <div v-if="data.reason" class="psc-reason">{{ data.reason }}</div>
        <div v-if="data.detail" class="psc-detail-text">{{ data.detail }}</div>
      </div>

      <div class="psc-pills">
        <button v-if="data.sales_play_id" class="psc-pill" @click="go(`/sales-plays/${data.sales_play_id}`)">
          <AppIcon name="sales_play" :size="12" />
          {{ data.sales_play_name }}
        </button>
        <button v-if="data.agent_run_id" class="psc-pill" @click="go(`/agent-runs/${data.agent_run_id}`)">
          <AppIcon name="agent" :size="12" />
          {{ runPillLabel }}
        </button>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-secondary" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'

const props = defineProps<{
  data: Record<string, any>
  createdAt?: string | null
  onClose?: () => void
}>()

const router = useRouter()
const data = computed(() => props.data || {})

const RUN_TYPE_LABELS: Record<string, string> = {
  source: 'Find',
  filter: 'Qualify',
  agent: 'Agent',
  chat: 'Chat',
  revision: 'Revision',
}

const runTypeLabel = computed(() => {
  const rt = data.value.agent_run_type
  return rt ? RUN_TYPE_LABELS[rt] || rt : ''
})

const triggeredBy = computed(() => {
  if (data.value.actor_user_name) return data.value.actor_user_name
  return data.value.agent_name || ''
})

const runPillLabel = computed(() => {
  return data.value.agent_name || runTypeLabel.value || 'Agent run'
})

const timestamp = computed(() => {
  if (!props.createdAt) return ''
  const d = new Date(props.createdAt)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
})

function onClose() {
  props.onClose?.()
}

function go(path: string) {
  router.push(path)
  onClose()
}
</script>

<style scoped>
.psc-body { display: flex; flex-direction: column; gap: 12px; }
.psc-fields { display: flex; flex-direction: column; gap: 4px; }
.psc-row { font-size: 14px; color: #111827; }
.psc-label { color: #6b7280; margin-right: 4px; }
.psc-label::after { content: ':'; }
.psc-type-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  vertical-align: baseline;
  margin-right: 4px;
}
.psc-type--agent  { background: #ede9fe; color: #6d28d9; }
.psc-type--source { background: #dbeafe; color: #1d4ed8; }
.psc-type--filter { background: #fef3c7; color: #92400e; }
.psc-type--chat   { background: #e0e7ff; color: #3730a3; }
.psc-type--revision { background: #f3e8ff; color: #7e22ce; }
.psc-type--user    { background: #d1fae5; color: #065f46; }
.psc-detail { display: flex; flex-direction: column; gap: 2px; }
.psc-manual-tag {
  display: inline-block;
  align-self: flex-start;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  background: #d1fae5;
  color: #065f46;
  margin-bottom: 2px;
}
.psc-reason { font-size: 14px; font-weight: 600; color: #111827; }
.psc-detail-text { font-size: 14px; color: #374151; }
.psc-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.psc-pill {
  display: inline-flex; align-items: center; gap: 6px;
  max-width: 100%;
  padding: 5px 10px;
  border: 1px solid #e5e7eb; border-radius: 999px;
  background: #f9fafb; color: #374151;
  font-size: 13px; cursor: pointer;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.psc-pill:hover { background: #f3f4f6; border-color: #d1d5db; color: #111827; }
</style>
